import { useEffect, useRef } from 'react';

// Elementos enfocables dentro del panel (alcanza para los modales de esta app).
const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Comportamiento accesible compartido de TODOS los modales de la app: foco al panel al
// abrir, Tab atrapado adentro, Escape cierra (si se pasa `onClose` — los modales
// bloqueantes como Welcome no lo pasan) y el foco vuelve a donde estaba al cerrar.
// Es un hook y no un componente porque lo compartido es el comportamiento: el markup y
// el estilo de cada modal son propios (bottom-sheet, card centrada, pantalla completa).
//
// `active`: para modales SIEMPRE montados que abren por estado (Intro, OracleTuning,
// Announcements, Welcome devuelven null hasta abrirse) — el efecto se dispara recién
// cuando el panel existe. Los que se montan al abrir (TimbaHelp, etc.) no lo pasan.
//
// Uso:  const panelRef = useModal(onClose);
//       <Panel ref={panelRef} role="dialog" aria-modal="true" aria-label="…" tabIndex={-1}>
export function useModal(onClose?: () => void, active = true) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose; // siempre la última versión, sin re-correr el efecto (no re-roba el foco)

  useEffect(() => {
    if (!active) return;
    const panel = panelRef.current;
    if (!panel) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panel.focus();

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (closeRef.current) {
          event.stopPropagation();
          closeRef.current();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      previouslyFocused?.focus();
    };
  }, [active]);

  return panelRef;
}
